import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Spacer,
  Text,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import {
  SuccessPortfolioCovenantGQLType,
  PortfolioCovenantVariableGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import {
  getCovenantValueFormula,
  getThresholdType,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantValue/thresholdValueUtils';
import { CovenantChartDetails } from 'packages/portfolio/ui/covenants/CovenantChartDetails';
import { expandCovenantsChartData } from 'packages/portfolio/ui/shared/portfolioChartUtils';
import {
  PortfolioCovenantPreviewResultsInputsAccordion,
  PortfolioCovenantPreviewResultsRecurringDatesAccordion,
} from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsPreviewDrawerComponents';

const groupVariablesByKeys = ({
  keys,
  portfolioCovenantPreviewResult,
  type,
}: {
  keys: string[];
  portfolioCovenantPreviewResult: SuccessPortfolioCovenantGQLType;
  type: 'input' | 'recurringDates';
}): {
  [key: string]: PortfolioCovenantVariableGQLType[];
} => {
  const groupedResults: {
    [key: string]: PortfolioCovenantVariableGQLType[];
  } = {};
  _.forEach(keys, (configKey) => {
    _.forEach(portfolioCovenantPreviewResult.variables, (variable) => {
      const variableNameSplit = variable.name.split('_');

      // if `idx_<index>` exists in the variable name, remove it
      if (
        _.startsWith(variableNameSplit[variableNameSplit.length - 1], 'idx')
      ) {
        variableNameSplit.pop();
      }

      let variableNameRoot = variableNameSplit.join('_');

      // inputs are named like `<inputName>.<columnName>_idx<index>`
      if (type === 'input') {
        variableNameRoot = variableNameRoot.split('.')[0];
      }

      if (variableNameRoot === configKey) {
        if (!groupedResults[configKey]) {
          groupedResults[configKey] = [];
        }

        if (!_.isNil(variable.value) && variable.value !== '') {
          groupedResults[configKey].push(variable);
        }
      }
    });
  });

  return groupedResults;
};

export const getRecurringDatesFromPortfolioCovenantResults = ({
  portfolioCovenantPreviewResult,
  creditAgreementPortfolioCovenant,
}: {
  portfolioCovenantPreviewResult: SuccessPortfolioCovenantGQLType;
  creditAgreementPortfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
}): {
  [key: string]: PortfolioCovenantVariableGQLType[];
} => {
  const recurringDateConfigKeys = _.map(
    creditAgreementPortfolioCovenant._config_?.globalRecurringDateConfigs,
    (recurringDateConfig) => recurringDateConfig.field,
  );

  return groupVariablesByKeys({
    keys: recurringDateConfigKeys,
    portfolioCovenantPreviewResult,
    type: 'recurringDates',
  });
};

export const getInputResultsFromPortfolioCovenantResults = ({
  portfolioCovenantPreviewResult,
  creditAgreementPortfolioCovenant,
}: {
  portfolioCovenantPreviewResult: SuccessPortfolioCovenantGQLType;
  creditAgreementPortfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
}): {
  [key: string]: PortfolioCovenantVariableGQLType[];
} => {
  const athenaQueryConfigKeys = _.map(
    creditAgreementPortfolioCovenant._config_?.athenaQueries,
    (athenaQuery) => athenaQuery.query.name,
  );
  const athenaRecurringQueryConfigKeys = _.map(
    creditAgreementPortfolioCovenant._config_?.athenaRecurringQueries,
    (athenaRecurringQuery) => athenaRecurringQuery.name,
  );
  const postgresQueryConfigKeys = _.map(
    creditAgreementPortfolioCovenant._config_?.postgresQueries,
    (postgresQuery) => postgresQuery.name,
  );

  const inputKeys = [
    ...athenaQueryConfigKeys,
    ...athenaRecurringQueryConfigKeys,
    ...postgresQueryConfigKeys,
  ];

  return groupVariablesByKeys({
    keys: inputKeys,
    portfolioCovenantPreviewResult,
    type: 'input',
  });
};

type SettingsFacilityPortfolioCovenantsPreviewDrawerProps = {
  currentPortfolioCovenantPreviewResult?: SuccessPortfolioCovenantGQLType | null;
  allPortfolioCovenantPreviewResults: SuccessPortfolioCovenantGQLType[];
  creditAgreementPortfolioCovenant: CreditAgreement.DomainEntity.PortfolioCovenantProperty;
  portfolioCovenantFullPropertyKey: string;
  isOpen: boolean;
  onClose: () => void;
  isLatestPreviewLoading: boolean;
};
export const SettingsFacilityPortfolioCovenantsPreviewDrawer = ({
  currentPortfolioCovenantPreviewResult,
  allPortfolioCovenantPreviewResults,
  creditAgreementPortfolioCovenant,
  portfolioCovenantFullPropertyKey,
  isOpen,
  onClose,
  isLatestPreviewLoading,
}: SettingsFacilityPortfolioCovenantsPreviewDrawerProps) => {
  const [
    selectedPreviewVersion,
    setSelectedPreviewVersion,
  ] = useState<SuccessPortfolioCovenantGQLType>(
    currentPortfolioCovenantPreviewResult ??
      allPortfolioCovenantPreviewResults[0],
  );
  const thresholdType =
    creditAgreementPortfolioCovenant &&
    creditAgreementPortfolioCovenant._config_
      ? getThresholdType(
          'portfolio',
          creditAgreementPortfolioCovenant._config_.intermediateFields,
        )
      : null;

  const portfolioCovenantValueFormula = getCovenantValueFormula({
    type: 'portfolio',
    creditAgreementCovenant: creditAgreementPortfolioCovenant,
  });

  const expandedPortfolioCovenantPreviewResult = expandCovenantsChartData([
    selectedPreviewVersion,
  ]);

  const recurringDatesVariableResults = getRecurringDatesFromPortfolioCovenantResults(
    {
      portfolioCovenantPreviewResult: selectedPreviewVersion,
      creditAgreementPortfolioCovenant,
    },
  );

  const inputVariableResults = getInputResultsFromPortfolioCovenantResults({
    portfolioCovenantPreviewResult: selectedPreviewVersion,
    creditAgreementPortfolioCovenant,
  });

  const orderedAllResults = _.orderBy(
    allPortfolioCovenantPreviewResults,
    ['triggerDate', 'version'],
    ['desc', 'desc'],
  );

  const previewVersionSelectOptions: {
    value: string;
    label: string;
  }[] = orderedAllResults.map((portfolioCovenantPreviewResult) => {
    return {
      value: portfolioCovenantPreviewResult.id,
      label: `${commonDate.fullDateStrToHumanDateString(
        portfolioCovenantPreviewResult.triggerDate,
      )} (Version ${portfolioCovenantPreviewResult.version})`,
    };
  });

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent padding="16px" backgroundColor={colors.background[100]}>
        <DrawerHeader>
          <Flex alignItems="center">
            <Heading as="h2" fontSize="24px" lineHeight="32px" fontWeight={600}>
              {`${creditAgreementPortfolioCovenant.covenantKey} Preview Results`}
            </Heading>
            <Spacer />
            <Button variant="ghost" onClick={onClose}>
              <Icon icon="x" />
            </Button>
          </Flex>
          {isLatestPreviewLoading && (
            <Alert status="info" mt="16px">
              <AlertIcon />
              <Text fontSize="16px">
                A newer version of this portfolio covenant preview is being
                generated.
              </Text>
            </Alert>
          )}
          <Flex alignItems="center" mt="16px">
            <Text fontSize="16px" fontWeight={500} mr="16px">
              Preview Trigger Date
            </Text>
            <Box fontSize="16px" backgroundColor="white" borderRadius="8px">
              <Select
                options={previewVersionSelectOptions}
                value={selectedPreviewVersion.id}
                onChange={(newPreviewVersionId) => {
                  if (newPreviewVersionId) {
                    const newPreviewVersionPortfolioCovenantResult = allPortfolioCovenantPreviewResults.find(
                      (portCovResult) =>
                        portCovResult.id === newPreviewVersionId,
                    );
                    if (newPreviewVersionPortfolioCovenantResult) {
                      setSelectedPreviewVersion(
                        newPreviewVersionPortfolioCovenantResult,
                      );
                    }
                  }
                }}
              />
            </Box>
          </Flex>

          {!!selectedPreviewVersion.updated_at && (
            <Text
              fontSize="14px"
              fontWeight={400}
              color={colors.black[70]}
              mt="16px"
            >
              {`Last previewed on ${commonDate.epochToLocaleDateString(
                selectedPreviewVersion.updated_at,
              )}`}
            </Text>
          )}
        </DrawerHeader>

        <DrawerBody>
          <Box
            width="100%"
            mt="24px"
            mb="32px"
            borderWidth="1px"
            borderRadius="8px"
            padding="24px"
            backgroundColor="white"
          >
            <CovenantChartDetails
              selectedDataPoint={expandedPortfolioCovenantPreviewResult[0]}
              thresholdType={thresholdType}
              covenantValueFormula={portfolioCovenantValueFormula}
            />
          </Box>
          <PortfolioCovenantPreviewResultsRecurringDatesAccordion
            recurringDatesResults={recurringDatesVariableResults}
            creditAgreementPortfolioCovenant={creditAgreementPortfolioCovenant}
            portfolioCovenantFullPropertyKey={portfolioCovenantFullPropertyKey}
          />
          <PortfolioCovenantPreviewResultsInputsAccordion
            inputResults={inputVariableResults}
            creditAgreementPortfolioCovenant={creditAgreementPortfolioCovenant}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
